import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Head from 'next/head';
import { CheckCircle2, ClipboardList, Lightbulb, Users } from 'lucide-react';
import { SizzleConciergeSection } from '@/components/HomeTradeFocus/SizzleConciergeSection';
import { ConciergeEnquiryButton } from '@/components/concierge/ConciergeEnquiryButton';
const HOW_IT_WORKS = [{
  icon: ClipboardList,
  title: 'Tell me about your business',
  description: "Share what you sell, where you're at, and what you're trying to achieve. A few lines is plenty to get started."
}, {
  icon: Lightbulb,
  title: 'Get a tailored analysis',
  description: "I'll take a proper look and come back with honest, independent suggestions you can act on."
}, {
  icon: Users,
  title: 'Connect with the right help',
  description: "If you need a hand making it happen, I'll introduce you to the expert partners best placed to grow your demand."
}];

/**
 * Standalone page for the founder-led concierge service. Reuses the home
 * {@link SizzleConciergeSection} as the hero and adds page-level framing
 * (meta, a "how it works" explainer) so the same experience works as a
 * destination rather than a home band.
 */
export default function ConciergePage() {
  const $ = _c(10);
  const [submitted, setSubmitted] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Head><title>Get a free tailored analysis and suggestions | Sizzle</title><meta name="description" content="Tell me about your food or drink brand or store and I'll come back with a free, independent, tailored analysis and suggestions, plus intros to the right expert help to grow demand." /></Head>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-auto max-w-5xl px-4 pb-6 pt-12 text-center sm:px-6 sm:pt-16 lg:px-8"><h1 className="mx-auto max-w-3xl text-4xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl">Free, independent, unbiased help to grow your business</h1><p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">No cost, no sales pitch. Just honest ideas tailored to your food or drink brand or store, and intros to the right expert help when you need it.</p></div>;
    t2 = <SizzleConciergeSection userSessionRequired={false} className="pb-8" tertiaryLabel={undefined} tertiaryHref={undefined} />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="text-center text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">How it works</h2>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-10 grid gap-6 sm:grid-cols-3">{HOW_IT_WORKS.map(_temp)}</div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ConciergeEnquiryButton userSessionRequired={false} label="Get a free tailored analysis and suggestions" className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-[#ff5a36] px-7 py-3.5 text-base font-black text-white shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/30" onSuccess={() => setSubmitted(true)} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== submitted) {
    t6 = submitted && <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-5 text-left"><CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" /><div><p className="text-base font-bold text-green-800">Thanks, I've got it</p><p className="mt-1 text-sm text-green-800/90">I'll get back to you soon with a tailored analysis and suggestions.</p></div></div>;
    $[6] = submitted;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <>{t0}<div className="min-h-screen bg-[#fffaf4]">{t1}{t2}<section className="py-16 lg:py-20"><div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">{t3}{t4}<div className="mt-10 text-center">{t5}{t6}</div></div></section></div></>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function _temp(step, index) {
  const Icon = step.icon;
  return <div key={step.title} className="rounded-2xl border border-orange-100 bg-white p-6 shadow-sm"><div className="flex items-center gap-3"><span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600"><Icon className="h-5 w-5" aria-hidden="true" /></span><span className="text-sm font-bold text-gray-400">Step {index + 1}</span></div><h3 className="mt-4 text-lg font-bold text-gray-900">{step.title}</h3><p className="mt-2 text-sm leading-relaxed text-gray-600">{step.description}</p></div>;
}